import axios from '@/utils/axios';
import { AuditLog, AuditLogChange } from '../types';

type PaginationMeta = {
  total?: number | null;
  totalPages?: number | null;
  page?: number | null;
  limit?: number | null;
  sortBy?: string | null;
  sortOrder?: string | null;
};

export type GetAuditLogsParams = Partial<{
  search: string;
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: string;
  user_info: string;
  userInfo: string;
  action: string;
  resource_name: string;
  resourceName: string;
  resource_id: string | number;
  resourceId: string | number;
  message: string;
  ip: string;
}>;

export type AuditLogsListResponse = {
  data: AuditLog[];
  meta?: PaginationMeta | null;
};

export async function getAuditLogs(
  params?: GetAuditLogsParams
): Promise<AuditLogsListResponse> {
  const response = await axios.get('/audit-logs', {
    params,
  });

  const payload = response?.data ?? {};
  const rawMeta = payload?.meta ?? payload?.pagination ?? null;
  const collectionCandidates = [
    payload?.data,
    payload?.results,
    payload?.items,
    payload?.logs,
    payload,
  ];

  const collection = collectionCandidates.find(Array.isArray) as
    | unknown[]
    | undefined;

  const normalizeMeta = (meta: unknown): PaginationMeta | null => {
    if (!meta || typeof meta !== 'object') {
      return null;
    }

    const casted = meta as Record<string, unknown>;

    const toNumber = (value: unknown): number | null => {
      if (typeof value === 'number') {
        return Number.isNaN(value) ? null : value;
      }

      if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
      }

      return null;
    };

    const toString = (value: unknown): string | null =>
      typeof value === 'string' && value.trim() ? value : null;

    return {
      total:
        toNumber(casted.total) ??
        toNumber(casted.total_count) ??
        toNumber(casted.totalCount) ??
        toNumber(casted.count) ??
        null,
      totalPages:
        toNumber(casted.totalPages) ??
        toNumber(casted.total_pages) ??
        toNumber(casted.last_page) ??
        toNumber(casted.pages) ??
        null,
      page:
        toNumber(casted.page) ??
        toNumber(casted.current_page) ??
        toNumber(casted.pageNumber) ??
        null,
      limit:
        toNumber(casted.limit) ??
        toNumber(casted.per_page) ??
        toNumber(casted.pageSize) ??
        null,
      sortBy: toString(casted.sortBy) ?? toString(casted.sort_by) ?? null,
      sortOrder:
        toString(casted.sortOrder) ?? toString(casted.sort_order) ?? null,
    };
  };

  const parseUserInfo = (
    userInfo: unknown
  ): { userName: string | null; userEmail: string | null } => {
    if (typeof userInfo !== 'string') {
      return { userName: null, userEmail: null };
    }

    const [namePart, emailPart] = userInfo
      .split('|')
      .map((part) => part.trim());

    return {
      userName: namePart?.length ? namePart : null,
      userEmail: emailPart?.length ? emailPart : null,
    };
  };

  const normalizeChanges = (changes: unknown): AuditLogChange[] => {
    if (!changes || typeof changes !== 'object') {
      return [];
    }

    return Object.entries(changes as Record<string, unknown>).map(
      ([field, value]) => {
        const payloadValue = value as
          | { from?: unknown; to?: unknown }
          | undefined;

        return {
          field,
          from: payloadValue?.from,
          to: payloadValue?.to,
        };
      }
    );
  };

  const parsePrimaryId = (
    value: unknown,
    fallbackIndex: number
  ): number | string => {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed) {
        return `audit-log-${fallbackIndex}`;
      }

      const numeric = Number(trimmed);
      return Number.isNaN(numeric) ? trimmed : numeric;
    }

    return `audit-log-${fallbackIndex}`;
  };

  const parseOptionalId = (value: unknown): number | string | null => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed) {
        return null;
      }

      const numeric = Number(trimmed);
      return Number.isNaN(numeric) ? trimmed : numeric;
    }

    return null;
  };

  const normalizedCollection = (collection ?? []).map((item, index) => {
    const auditLog = item as Record<string, unknown>;
    const { userName, userEmail } = parseUserInfo(auditLog.user_info);

    return {
      id: parsePrimaryId(auditLog.id, index),
      userId: parseOptionalId(auditLog.user_id),
      userName,
      userEmail,
      action: typeof auditLog.action === 'string' ? auditLog.action : '',
      resourceName:
        typeof auditLog.resource_name === 'string'
          ? auditLog.resource_name
          : null,
      resourceId: parseOptionalId(auditLog.resource_id),
      message: typeof auditLog.message === 'string' ? auditLog.message : null,
      changes: normalizeChanges(auditLog.changes),
      ip: typeof auditLog.ip === 'string' ? auditLog.ip : null,
      createdAt:
        typeof auditLog.created_at === 'string'
          ? auditLog.created_at
          : auditLog.created_at instanceof Date
          ? auditLog.created_at.toISOString()
          : '',
    } satisfies AuditLog;
  });

  return {
    data: normalizedCollection,
    meta: normalizeMeta(rawMeta),
  };
}
